import { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { MultiValue, SingleValue } from 'react-select'
import { CompanyListItem, SelectItem } from '../../charts/core/_models'
import ApexChart from 'react-apexcharts'
import { getAlarmDataForCalendarView } from '../core/_requests'
import { AlarmDataForCalendarModal } from '../core/_models'
import { AlarmCommentForm } from './AlarmCommentForm'
import { useAuth } from '../../auth/core/AuthProvider'
interface AlarmProps {
  onClose: () => void
  isOpen: boolean
  date: string
  company: SingleValue<CompanyListItem>
  companySites: MultiValue<CompanyListItem>
  meters: MultiValue<SelectItem>
  alarmType: 'daily' | 'hourly' | null
}

const CalendarAlarmViewModal: FC<AlarmProps> = ({
  onClose,
  isOpen,
  date,
  company,
  companySites,
  meters,
  alarmType,
}) => {
  const { user } = useAuth()
  const canEditAlarms = user?.permissions?.canManageSiteData ?? false
  const params = {
    isLoggedIn: true,
    companyId: company ? company.value : '',
    siteId: companySites,
    metersId: meters,
    meterType: 'Electricity',
    selectedDate: date,
    alarmType: alarmType ?? 'daily',
  }

  let [alarmDataDetail, setAlarmDataDetail] = useState<AlarmDataForCalendarModal>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const fetchAlarmDataForCalendar = async () => {
    try {
      setIsLoading(true)
      getAlarmDataForCalendarView({
        ...params,
        isLoggedIn: false,
      }).then((response) => {
        setTimeout(async () => {
          let data = await getAlarmDataForCalendarView(params)
          setAlarmDataDetail(data)
          setIsLoading(false)
        }, 2000)
      })
    } catch (error) {
      console.log('Error fetching cell chart data:', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (company && date) {
      fetchAlarmDataForCalendar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, companySites, meters, date, alarmType])

  const handleCloseModal = () => {
    onClose()
  }
  const chartData = {
    options: {
      chart: {
        zoom: {
          enabled: true,
        },
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 2,
        strokeWidth: 0,
        fillOpacity: 1,
      },
      stroke: {
        curve: 'smooth' as const,
      },
      colors: ['#0070FF', '#50CD89', '#FFA500'],
      xaxis: {
        categories: [],
        tickAmount: 12,
        labels: {
          style: {
            fontSize: '12px',
          },
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          height: 6,
          offsetX: 0,
          offsetY: 0,
        },
      },
      legend: {
        show: true,
        position: 'bottom' as const,
        horizontalAlign: 'center' as const,
      },
    },
  }

  type organizedDataType = {
    site_name: string
    site_alarm_id: number
    site_data: any
    meters: any[]
    labels: string[]
  }
  const organizedData: organizedDataType[] = []
  let i = 0
  for (let alarm in alarmDataDetail) {
    organizedData[i] = {
      site_name: alarm,
      site_alarm_id: alarmDataDetail[alarm]['site_alarm_id'],
      site_data: alarmDataDetail[alarm]['site_data'],
      meters: Object.keys(alarmDataDetail[alarm]['meters']).map((key) => ({
        meter: key,
        data: alarmDataDetail[alarm]['meters'][key],
      })),
      labels: alarmDataDetail[alarm]['labels'] || [],
    }
    i = i + 1
  }
  function formatDate(date: any) {
    const dateParts = date.split('-')
    const year = dateParts[0]
    const month = dateParts[1]
    const day = dateParts[2]

    const formattedDate = new Date(`${year}-${month}-${day}`)

    const formattedDateString = `${formattedDate.getDate()}-${
      formattedDate.getMonth() + 1
    }-${formattedDate.getFullYear()}`

    return formattedDateString
  }
  const formattedDate = formatDate(date)

  type ApexSeries = { name: string; data: any[] }
  const reorderSeries = (series: any): ApexSeries[] => {
    const list: ApexSeries[] = Array.isArray(series) ? series : []
    const average = list.find(
      (s) => s && (s.name === 'Average' || s.name?.toLowerCase?.() === 'average')
    )
    const threshold = list.find(
      (s) => s && (s.name === 'Threshold' || s.name?.toLowerCase?.() === 'threshold')
    )
    const selected = list.filter(
      (s) =>
        s &&
        !(s.name === 'Average' || s.name?.toLowerCase?.() === 'average') &&
        !(s.name === 'Threshold' || s.name?.toLowerCase?.() === 'threshold')
    )
    const ordered: ApexSeries[] = []
    if (selected && selected.length > 0) ordered.push(selected[0])
    if (average) ordered.push(average)
    if (threshold) ordered.push(threshold)
    const result = ordered.length > 0 ? ordered : list

    // Normalize legend names to capitalized variants for consistency
    const normalizeLegendName = (name: any) => {
      const lower = typeof name === 'string' ? name.toLowerCase() : ''
      if (lower === 'today') return 'Today'
      if (lower === 'average') return 'Average'
      if (lower === 'threshold') return 'Threshold'
      return name
    }

    return result.map((s) => ({ ...s, name: normalizeLegendName(s?.name) }))
  }

  const toNumber = (val: any) => (typeof val === 'number' ? val : Number(val) || 0)
  const sumArray = (arr: any[]) =>
    Array.isArray(arr) ? arr.reduce((a, b) => a + toNumber(b), 0) : 0
  const extractDailyCards = (series: any) => {
    const list: ApexSeries[] = Array.isArray(series) ? series : []
    const normalizeName = (n: any) => (typeof n === 'string' ? n.toLowerCase() : '')
    const today = list.find((s) => normalizeName(s?.name) === 'today')
    const average = list.find((s) => normalizeName(s?.name) === 'average')
    const threshold = list.find((s) => normalizeName(s?.name) === 'threshold')
    const todayValue = today
      ? Array.isArray(today.data)
        ? sumArray(today.data)
        : toNumber(today.data)
      : 0
    const averageValue = average
      ? Array.isArray(average.data)
        ? sumArray(average.data)
        : toNumber(average.data)
      : 0
    const thresholdValue = threshold
      ? Array.isArray(threshold.data)
        ? sumArray(threshold.data)
        : toNumber(threshold.data)
      : 0
    return { todayValue, averageValue, thresholdValue }
  }

  return (
    <Modal
      id='kt_modal_create_alarms'
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered'
      size='xl'
      show={isOpen}
      backdrop={true}
    >
      <Modal.Header closeButton onClick={handleCloseModal}>
        <Modal.Title>Alarm Details for {formattedDate}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && (
          <div className='mb-4' style={{ textAlign: 'center' }}>
            <span
              className='indicator-progress'
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              Loading...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          </div>
        )}
        <div className='accordion' id='outerAccordion'>
          {organizedData.map((data, dataIndex) => (
            <div className='accordion-item' key={`site-${dataIndex}`}>
              <h2 className='accordion-header' id={`site${dataIndex}Header`}>
                <button
                  className='accordion-button collapsed'
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target={`#site${dataIndex}Collapse`}
                  aria-expanded='false'
                  aria-controls={`site${dataIndex}Collapse`}
                >
                  {data.site_name}
                </button>
              </h2>
              <div
                id={`site${dataIndex}Collapse`}
                className='accordion-collapse collapse'
                aria-labelledby={`site${dataIndex}Header`}
                data-bs-parent='#outerAccordion'
              >
                <div className='accordion-body'>
                  <div className='accordion-body'>
                    {alarmType === 'daily' ? (
                      <div className='row g-3'>
                        {(() => {
                          const values = extractDailyCards(data.site_data)
                          return (
                            <>
                              <div className='col-md-4'>
                                <div className='card border-0 bg-light'>
                                  <div className='card-body text-center py-3'>
                                    <h6 className='text-muted mb-1'>Daily Total Consumption</h6>
                                    <h4 className='text-primary fw-bold mb-0'>
                                      {values.todayValue}
                                      <small className='text-muted ms-1'>kWh</small>
                                    </h4>
                                  </div>
                                </div>
                              </div>
                              <div className='col-md-4'>
                                <div className='card border-0 bg-light'>
                                  <div className='card-body text-center py-3'>
                                    <h6 className='text-muted mb-1'>Average</h6>
                                    <h4 className='text-success fw-bold mb-0'>
                                      {values.averageValue}
                                      <small className='text-muted ms-1'>kWh</small>
                                    </h4>
                                  </div>
                                </div>
                              </div>
                              <div className='col-md-4'>
                                <div className='card border-0 bg-light'>
                                  <div className='card-body text-center py-3'>
                                    <h6 className='text-muted mb-1'>Threshold</h6>
                                    <h4 className='text-warning fw-bold mb-0'>
                                      {values.thresholdValue}
                                      <small className='text-muted ms-1'>kWh</small>
                                    </h4>
                                  </div>
                                </div>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    ) : (
                      <ApexChart
                        options={{
                          ...chartData.options,
                          xaxis: {
                            ...chartData.options.xaxis,
                            categories: data.labels || chartData.options.xaxis.categories,
                          },
                        }}
                        series={reorderSeries(data.site_data)}
                        type='line'
                        height={300}
                      />
                    )}
                  </div>

                  {canEditAlarms && (
                    <div>
                      <AlarmCommentForm alarm_id={data.site_alarm_id} />
                    </div>
                  )}
                  <div className='accordion' key={`meter-more}`}>
                    <h2 className='accordion-header' id={`meter-moreHeader`}>
                      <button
                        className='accordion-button collapsed'
                        type='button'
                        data-bs-toggle='collapse'
                        data-bs-target={`#meter-moreCollapse`}
                        aria-expanded='false'
                        aria-controls={`meter-moreCollapse`}
                        style={{ backgroundColor: '#f9f9f9' }}
                      >
                        More Details Based on Meters
                      </button>
                    </h2>
                    <div
                      id={`meter-moreCollapse`}
                      className='accordion-collapse collapse'
                      aria-labelledby={`meter-moreHeader`}
                    >
                      <div className='accordion-body'>
                        {Array.isArray(data.meters) ? (
                          data.meters.map((item) => (
                            <div className='accordion' key={`meter-${item.meter}`}>
                              <h2 className='accordion-header' id={`meter${item.meter}Header`}>
                                <button
                                  className='accordion-button collapsed'
                                  type='button'
                                  data-bs-toggle='collapse'
                                  data-bs-target={`#meter${item.meter}Collapse`}
                                  aria-expanded='false'
                                  aria-controls={`meter${item.meter}Collapse`}
                                >
                                  Meter:{item.meter}
                                </button>
                              </h2>
                              <div
                                id={`meter${item.meter}Collapse`}
                                className='accordion-collapse collapse'
                                aria-labelledby={`meter${item.meter}Header`}
                              >
                                <div className='accordion-body'>
                                  {alarmType === 'daily' ? (
                                    <div className='row g-3'>
                                      {(() => {
                                        const values = extractDailyCards(item.data)
                                        return (
                                          <>
                                            <div className='col-md-4'>
                                              <div className='card border-0 bg-light'>
                                                <div className='card-body text-center py-3'>
                                                  <h6 className='text-muted mb-1'>
                                                    Daily Total Consumption
                                                  </h6>
                                                  <h4 className='text-primary fw-bold mb-0'>
                                                    {values.todayValue}
                                                    <small className='text-muted ms-1'>kWh</small>
                                                  </h4>
                                                </div>
                                              </div>
                                            </div>
                                            <div className='col-md-4'>
                                              <div className='card border-0 bg-light'>
                                                <div className='card-body text-center py-3'>
                                                  <h6 className='text-muted mb-1'>Average</h6>
                                                  <h4 className='text-success fw-bold mb-0'>
                                                    {values.averageValue}
                                                    <small className='text-muted ms-1'>kWh</small>
                                                  </h4>
                                                </div>
                                              </div>
                                            </div>
                                            <div className='col-md-4'>
                                              <div className='card border-0 bg-light'>
                                                <div className='card-body text-center py-3'>
                                                  <h6 className='text-muted mb-1'>Threshold</h6>
                                                  <h4 className='text-warning fw-bold mb-0'>
                                                    {values.thresholdValue}
                                                    <small className='text-muted ms-1'>kWh</small>
                                                  </h4>
                                                </div>
                                              </div>
                                            </div>
                                          </>
                                        )
                                      })()}
                                    </div>
                                  ) : (
                                    <ApexChart
                                      options={{
                                        ...chartData.options,
                                        xaxis: {
                                          ...chartData.options.xaxis,
                                          categories:
                                            data.labels || chartData.options.xaxis.categories,
                                        },
                                      }}
                                      series={reorderSeries(item.data)}
                                      type='line'
                                      height={300}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className='accordion-item'>
                            <h2 className='accordion-header'>
                              <button className='accordion-button' type='button' disabled>
                                Data not found.
                              </button>
                            </h2>
                            <div className='accordion-body'>Data not found.</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-content-end'>
        <button className='btn btn-danger btn-sm' onClick={handleCloseModal}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default CalendarAlarmViewModal
